<script setup>
/**
 * SzSenderBadge — pill com nome/avatar do remetente dentro do balão.
 *
 * Comportamento por tipo:
 *  - OUTGOING (agente respondendo): mostra agente que enviou.
 *  - INCOMING numa conversa de GRUPO (@g.us): mostra o NOME do CONTATO
 *    individual extraído do content (`*Nome*:\n...`), avatar com inicial
 *    do nome, e o número embaixo. Pill inteira clicável → abre o modal
 *    "Nova mensagem" pré-preenchido via window.__szOpenNewMessage().
 *  - INCOMING numa conversa individual: comportamento original (mostra
 *    o sender do payload, normalmente o próprio contato).
 *
 * Marker: SZ_SENDER_BADGE_GROUP_v2_20260520
 */
import { computed } from 'vue';
import { useStore } from 'vuex';

const props = defineProps({
  sender: { type: Object, default: null },
  messageType: { type: Number, default: 0 },
  isPrivate: { type: Boolean, default: false },
  content: { type: String, default: '' },
  contentAttributes: { type: [Object, String], default: () => ({}) },
});

const store = useStore();

const isIncoming = computed(() => props.messageType === 0);
const isOutgoing = computed(() => props.messageType === 1);

// Detecta se a conversa atual é grupo (identifier termina com @g.us)
const isGroupConversation = computed(() => {
  const id =
    store.getters?.getSelectedChat?.meta?.sender?.identifier || '';
  return String(id).endsWith('@g.us');
});

// content_attributes pode chegar como string em casos defensivos
const attrsObj = computed(() => {
  const v = props.contentAttributes;
  if (!v) return {};
  if (typeof v === 'string') {
    try { return JSON.parse(v) || {}; } catch (e) { return {}; }
  }
  return v;
});

// Chatwoot frontend converte snake_case do backend pra camelCase
const senderJid = computed(
  () => attrsObj.value?.senderJid || attrsObj.value?.sender_jid || ''
);

const phoneDigits = computed(() => {
  const jid = String(senderJid.value || '');
  if (!jid) return '';
  return (jid.split('@')[0] || '').replace(/\D/g, '');
});

const nameFromContent = computed(() => {
  const c = String(props.content || '');
  const m = c.match(/^\*([^*]+)\*\s*:\s*\n?/);
  return m ? m[1].trim() : '';
});

// Modo "group sender": só ativa em INCOMING dentro de conversa de grupo
// E com dados suficientes (phone ou nome) pra valer a pena renderizar
const useGroupSender = computed(
  () =>
    isIncoming.value &&
    isGroupConversation.value &&
    (!!phoneDigits.value || !!nameFromContent.value)
);

// ── Group sender display ──
const groupSenderName = computed(
  () => nameFromContent.value || phoneDigits.value || 'Participante'
);

const groupSenderInitials = computed(() => {
  const src = nameFromContent.value || '';
  if (!src) return '#';
  return src
    .split(/\s+/)
    .filter(Boolean)
    .map(w => w.charAt(0))
    .join('')
    .substring(0, 2)
    .toUpperCase();
});

// ── Fallback (comportamento original — agente/sender do payload) ──
const senderName = computed(() => {
  if (!props.sender) return '';
  return props.sender.name || props.sender.available_name || '';
});

const senderInitials = computed(() => {
  if (!senderName.value) return '';
  return senderName.value
    .split(' ')
    .map(w => w.charAt(0))
    .join('')
    .substring(0, 2)
    .toUpperCase();
});

const senderAvatar = computed(() => {
  if (!props.sender) return '';
  return props.sender.thumbnail || props.sender.avatar_url || '';
});

const isBot = computed(() => {
  if (!props.sender) return true;
  return props.sender.type === 'agent_bot';
});

const isVisible = computed(() => {
  if (useGroupSender.value) return true;
  return senderName.value && !isBot.value;
});

async function handleGroupSenderClick() {
  const phone = phoneDigits.value;
  if (!phone) return;
  const name = nameFromContent.value;
  const currentInboxId = store.getters?.getSelectedChat?.inbox_id || null;
  const accountId = store.getters?.getCurrentAccountId;
  const token = store.getters?.getCurrentUser?.access_token;

  // SZ_GROUP_PILL_v2_20260522: caminho rápido — busca conversa existente
  // no MESMO inbox do grupo. Se achar, navega direto (sem modal).
  // Se contato existir mas sem conversa, passa pro modal pra ele pular etapa 1.
  let prefillContact = null;
  if (accountId && token && currentInboxId) {
    try {
      const sUrl = `/api/v1/accounts/${accountId}/contacts/search?q=${encodeURIComponent(phone)}&include_contacts=true`;
      const sResp = await fetch(sUrl, { headers: { api_access_token: token } });
      const sData = await sResp.json();
      const contacts = sData.payload || [];
      const contact = contacts.find(c => {
        const ph = String(c.phone_number || '').replace(/\D/g, '');
        const id = String(c.identifier || '');
        return ph.endsWith(phone) || ph === phone || id.includes(phone);
      });
      if (contact && contact.id) {
        prefillContact = contact;
        const cUrl = `/api/v1/accounts/${accountId}/contacts/${contact.id}/conversations`;
        const cResp = await fetch(cUrl, { headers: { api_access_token: token } });
        const cData = await cResp.json();
        const convs = cData.payload || [];
        const matched = convs.filter(cv => cv.inbox_id === currentInboxId);
        if (matched.length > 0) {
          // Qualquer status (open/pending/snoozed/resolved). Mais recente vence.
          matched.sort((a, b) =>
            (b.last_activity_at || b.created_at || 0) -
            (a.last_activity_at || a.created_at || 0)
          );
          const conv = matched[0];
          const path = `/app/accounts/${accountId}/conversations/${conv.id}`;
          const routerEl = document.querySelector('#app');
          const router = routerEl?.__vue_app__?.config?.globalProperties?.$router;
          if (router) {
            router.push(path).catch(() => { window.location.href = path; });
          } else {
            window.location.href = path;
          }
          return;
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[SzSenderBadge] lookup error, fallback to modal:', e);
    }
  }

  // Fallback: sem conversa no inbox do grupo → modal só com canal.
  // Passa o contato (se achado) via window pro modal pular a etapa de busca.
  if (prefillContact) {
    try { window.__szPrefillContact = prefillContact; } catch (e) {}
  }
  if (typeof window.__szOpenNewMessage === 'function') {
    try {
      window.__szOpenNewMessage(phone, name, { channelOnly: true });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[SzSenderBadge] __szOpenNewMessage error:', e);
    }
  }
}
</script>

<template>
  <!-- Modo GRUPO (incoming): pill clicável com inicial + nome + número -->
  <button
    v-if="useGroupSender"
    type="button"
    class="sz-sender-badge sz-sender-badge--group"
    :title="phoneDigits ? `Abrir nova mensagem para ${phoneDigits}` : ''"
    @click.stop="handleGroupSenderClick"
  >
    <span class="sz-sender-initials sz-sender-initials--group">{{ groupSenderInitials }}</span>
    <span class="sz-sender-group-meta">
      <span class="sz-sender-name sz-sender-name--group">{{ groupSenderName }}</span>
      <span v-if="phoneDigits" class="sz-sender-phone">{{ phoneDigits }}</span>
    </span>
  </button>

  <!-- Modo padrão (outgoing, ou incoming de conversa individual) -->
  <div v-else-if="isVisible" class="sz-sender-badge">
    <span v-if="senderAvatar" class="sz-sender-avatar">
      <img :src="senderAvatar" :alt="senderInitials" />
    </span>
    <span v-else class="sz-sender-initials">{{ senderInitials }}</span>
    <span class="sz-sender-name">{{ senderName }}</span>
  </div>
</template>

<style>
/* SZ_SENDER_BADGE_GROUP_v2_20260520 — pill clicável de remetente de grupo.
   Cores alinhadas com o sz-sender-badge original (solarz_override.css):
   bg #EBEBEB, texto #1C2024. Avatar com cor própria pra contrastar com o bg. */
.sz-sender-badge--group {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #EBEBEB;
  border: 0;
  border-radius: 20px;
  padding: 0 10px 0 2px;
  margin: 0 0 2px 0;
  font-family: inherit;
  font-size: 12px;
  font-weight: 600;
  color: #1C2024;
  line-height: 1;
  text-align: left;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.12s ease, opacity 0.12s ease;
}

.dark .sz-sender-badge--group {
  background: #00000024;
  color: #EDEEEF;
}

.sz-sender-badge--group:hover {
  background: #DEDEDE;
}

.dark .sz-sender-badge--group:hover {
  background: #00000040;
}

.sz-sender-badge--group:hover .sz-sender-name--group {
  text-decoration: underline;
}

.sz-sender-initials--group {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  min-width: 24px;
  min-height: 24px;
  border-radius: 50%;
  background: #60646C;
  color: #FFFFFF;
  font-size: 10px;
  font-weight: 700;
  flex-shrink: 0;
  line-height: 1;
}

.dark .sz-sender-initials--group {
  background: #4A4D55;
  color: #FFFFFF;
}

.sz-sender-group-meta {
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
  line-height: 1;
  min-width: 0;
}

.sz-sender-name--group {
  color: inherit;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.01em;
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sz-sender-phone {
  color: inherit;
  font-size: 11px;
  font-weight: 500;
  opacity: 0.65;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}
</style>
